import { BusinessObjectBase } from './base';
import { serviceTaskHistorySchema } from '@schemas/business-objects/service-task-history';
import type { ServiceTask } from './service-task';
import type { Tenant } from './tenant';
import type {
  HistoryData,
  Id,
  ServiceTaskHistoryEventType,
} from '../base-types';

export interface ServiceTaskHistoryData {
  readonly id: Id;
  readonly eventType: ServiceTaskHistoryEventType;
  readonly data?: HistoryData;
  readonly timestamp?: Date;
  readonly serviceTaskId: Id;
  readonly serviceTask?: ServiceTask;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface ServiceTaskHistory extends Readonly<ServiceTaskHistoryData> {}
type ServiceTaskHistoryCreateWithChildrenData = Partial<ServiceTaskHistoryData>;

export class ServiceTaskHistory extends BusinessObjectBase {
  static readonly entityName = 'ServiceTaskHistory' as const;
  static readonly plural = 'service-task-histories';
  static schema = serviceTaskHistorySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ServiceTaskHistory[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ServiceTaskHistory(data));
  }

  static async findOne(filter?: any): Promise<ServiceTaskHistory | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ServiceTaskHistory(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ServiceTaskHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ServiceTaskHistory(data);
  }

  static async create(
    data: Omit<ServiceTaskHistoryData, 'id'>,
  ): Promise<ServiceTaskHistory> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ServiceTaskHistory(result);
  }

  static async createMany(
    data: Omit<ServiceTaskHistoryData, 'id'>[],
  ): Promise<ServiceTaskHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ServiceTaskHistory(item));
  }

  static async createWithChildren(
    data: ServiceTaskHistoryCreateWithChildrenData,
  ): Promise<ServiceTaskHistory> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ServiceTaskHistory(result);
  }

  static async createManyWithChildren(
    data: ServiceTaskHistoryCreateWithChildrenData[],
  ): Promise<ServiceTaskHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ServiceTaskHistory(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ServiceTaskHistoryData>,
  ): Promise<ServiceTaskHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ServiceTaskHistory(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
